import React, { FC } from 'react';

import styled from 'styled-components';

import Accordion, { AccordionItem } from '@adsk/alloy-react-accordion';
import { Box, Text } from '@adsk/alloy-react-box';
import { displayDate } from '@adsk/alloy-react-datetime';

import { useTranslation } from '../../i18n';
import { TRIAL, dayExpression } from './helpers/consts';
import {
  accordionItem,
  accordionContainer,
  titleText,
  secondaryText,
  labelText,
  footer,
  footerSideText,
  footerDateText,
} from './helpers/styles';
import { Offering } from '../../helpers/types';

interface SubscriptionItemProps {
  subscription: Offering;
  expanded?: boolean;
  onExpand(): void;
}

export const StyledLi = styled.li(secondaryText);

const SubscriptionItem: FC<SubscriptionItemProps> = ({
  subscription,
  expanded = false,
  onExpand,
}) => {
  const {
    offeringName,
    type,
    products,
    daysRemaining,
    endDate,
    sheetCount,
    alternativeSubscription,
  } = subscription;

  const { t, i18n } = useTranslation();
  const itemTitle = <Text style={titleText}>{offeringName}</Text>;

  const isTrial = type === TRIAL;
  const dayText = daysRemaining && t(dayExpression(daysRemaining));

  const alternativeTrialTime = t('TRIAL_EXPIRE_TIME', {
    numberOfDays: alternativeSubscription?.daysRemaining,
    noun: dayText,
  });

  const trialTime = t('TRIAL_EXPIRE_TIME', {
    numberOfDays: daysRemaining,
    noun: dayText,
  });

  const subscriptionTime = endDate
    ? t('SUBSCRIPTION_EXPIRE_TIME', {
        date: displayDate(new Date(endDate), {
          format: 'numeric',
          locale: i18n.language,
        }),
      })
    : t('SUBSCRIPTION_RENEWAL_AUTOMATICALLY');

  const sheets = sheetCount ? (
    <Text style={labelText}>{`${t('SHEET_COUNT')} ${sheetCount}`}</Text>
  ) : (
    ''
  );

  const AlternativeSpan = () => {
    if (!alternativeSubscription) return null;
    return (
      <Box style={footer} marginTop="8px">
        <Text style={footerSideText}> {t('TRIAL')}</Text>
        <Text style={footerDateText}>{alternativeTrialTime}</Text>
      </Box>
    );
  };

  const SubscriptionSpan = () => {
    return (
      <>
        <Text style={footerSideText}> {t('SUBSCRIPTION')}</Text>
        <Text style={footerDateText} data-testid="SubscriptionItem_TimeLabel">
          {subscriptionTime}
        </Text>
      </>
    );
  };

  const BottomSpan = () => {
    if (isTrial) {
      return (
        <>
          <Text style={footerSideText}> {t('TRIAL')}</Text>
          <Text style={footerDateText}>{trialTime}</Text>
        </>
      );
    }

    return <SubscriptionSpan />;
  };

  return (
    <div data-testid={subscription.offeringName}>
      <Accordion
        data-testid="SubscriptionItem__Accordion"
        initialExpanded={[1]}
        multiExpand
      >
        <AccordionItem
          data-testid="SubscriptionItem__AccordionItem"
          expanded={expanded}
          onExpand={onExpand}
          title={itemTitle}
          style={accordionItem}
          label={sheets}
        >
          {products?.length > 0 && (
            <Box
              style={accordionContainer}
              data-testid="SubscriptionItem_AccordionContainer"
            >
              <Text style={secondaryText}>{t('INCLUDED_IN_SUBSCRIPTION')}</Text>
              <ul>
                {products.map(({ productName, productKey }) => (
                  <StyledLi key={productKey}>{productName}</StyledLi>
                ))}
              </ul>
            </Box>
          )}
        </AccordionItem>
      </Accordion>

      <Box style={footer} data-testid="subscriptionItem">
        <BottomSpan />
      </Box>
      <AlternativeSpan />

      <br />
    </div>
  );
};

SubscriptionItem.displayName = 'SubscriptionItem';

export default SubscriptionItem;
